'use client';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useReducedMotion } from 'framer-motion';
import { effects, projects, ui, type T } from '../data';
import { Arrow } from './Icons';
import { LangToggle, useLang } from './Lang';
import KineticTitle from './KineticTitle';
import { Counter } from './PortfolioHome';
import { Fade, Wave, Words } from './Reveal';

const ShaderImage = dynamic(() => import('./ShaderImage'), { ssr: false });

export default function CaseStudy({ slug }: { slug: string }) {
    const { lang, t } = useLang();
    const reduce = useReducedMotion();
    const i = projects.findIndex((p) => p.slug === slug);
    const p = projects[i];
    const next = projects[(i + 1) % projects.length];
    const isLabel = p.category === 'packaging';
    const isSocial = p.category === 'social';
    const range = isLabel ? [p.cover, ...p.gallery] : p.gallery;
    const stats = p.stats?.filter((s) => s.value !== null) ?? [];

    const meta = [
        [ui.client, p.client],
        [ui.place, t(p.place)],
        [ui.year, p.year],
        [ui.roleLabel, t(p.role)],
        [ui.deliverables, t(p.deliverables)],
    ].filter(([, value]) => value) as [T, string][];
    const story = [
        [ui.context, p.context],
        [ui.brief, p.brief],
        [ui.process, p.process],
        [ui.result, p.result],
    ].filter(([, text]) => text.fr) as [T, T][];

    return (
        <main lang={lang} className="overflow-x-clip">
            <section className="px-4 pt-8 pb-16 md:px-10 md:pt-10">
                <div className="mb-12 flex flex-wrap items-center justify-between gap-4 text-sm">
                    <Link href={`/portfolio?lang=${lang}#work`} className="pf-link inline-flex items-center gap-2">
                        <Arrow dir="left" /> {t(ui.back)}
                    </Link>
                    <LangToggle />
                </div>
                <KineticTitle lines={p.title.length > 12 ? [p.title.slice(0, p.title.lastIndexOf(' ')), p.title.slice(p.title.lastIndexOf(' ') + 1)] : [p.title]} className="text-[17vw] md:text-[13vw]" />
                <Words as="p" text={t(p.summary)} delay={0.4} className="mt-8 max-w-4xl font-(family-name:--pf-serif) text-3xl leading-tight text-(--pf-wine) italic md:text-5xl" />
                <dl className="mt-14 grid gap-6 border-t border-(--pf-ink)/20 pt-6 sm:grid-cols-2 lg:grid-cols-5">
                    {meta.map(([label, value], i) => (
                        <Fade key={label.fr} delay={0.6 + i * 0.07}>
                            <dt className="mb-1 text-sm text-(--pf-ink)/65">{t(label)}</dt>
                            <dd className="text-sm leading-6">{value}</dd>
                        </Fade>
                    ))}
                </dl>
            </section>

            <section className="grid gap-12 px-4 pb-24 md:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
                {/* Visuel principal : l'effet propre au métier (trame, gaufrage, pixels, fil de fer) apparaît sous le curseur */}
                <figure className="lg:sticky lg:top-24 lg:self-start">
                    <div
                        className="relative mx-auto cursor-crosshair overflow-hidden"
                        style={{ aspectRatio: `${p.cover.w} / ${p.cover.h}`, width: `min(100%, calc(85vh * ${p.cover.w / p.cover.h}))` }}
                    >
                        <Image src={p.cover.src} alt={p.cover.alt} fill priority sizes="(min-width: 1024px) 55vw, 100vw" className="object-cover" />
                        {!reduce && <ShaderImage src={p.cover.src} effect={effects[p.category]} mode="reveal" />}
                    </div>
                    {p.cover.caption && <figcaption className="mt-3 text-sm">{t(p.cover.caption)}</figcaption>}
                </figure>
                <div className="space-y-12">
                    {story.map(([label, text]) => (
                        <div key={label.fr}>
                            <Words text={t(label)} className="mb-3 font-(family-name:--pf-serif) text-2xl text-(--pf-wine) italic" />
                            <Fade delay={0.15}>
                                <p className="max-w-[60ch] text-xl leading-9 md:text-2xl md:leading-10">{t(text)}</p>
                            </Fade>
                        </div>
                    ))}
                    {stats.length > 0 && (
                        <dl className="flex flex-wrap gap-x-12 gap-y-6 border-t border-(--pf-ink)/20 pt-6">
                            {stats.map((s) => (
                                <div key={s.label.fr}>
                                    <dd className="font-(family-name:--pf-serif) text-5xl tabular-nums"><Counter stat={s} /></dd>
                                    <dt className="mt-1 text-sm text-(--pf-ink)/70">{t(s.label)}</dt>
                                </div>
                            ))}
                        </dl>
                    )}
                    {p.link && (
                        <a href={p.link} target="_blank" rel="noopener noreferrer" className="pf-link inline-flex items-center gap-2 text-sm">
                            {t(p.category === 'social' ? ui.visitAccount : ui.visit)} <Arrow dir="up-right" />
                        </a>
                    )}
                </div>
            </section>

            {p.video && (
                <section className="px-4 pb-16 md:px-10">
                    <video className="w-full" autoPlay muted loop playsInline poster={p.gallery[0]?.src}>
                        <source src={p.video.replace('.mp4', '.webm')} type="video/webm" />
                        <source src={p.video} type="video/mp4" />
                    </video>
                </section>
            )}

            {range.length > (isLabel ? 1 : 0) && (
                <section className="px-4 pb-24 md:px-10">
                    {isLabel && <Words text={t(ui.range)} className="mb-10 border-t border-(--pf-ink)/20 pt-6 font-(family-name:--pf-serif) text-5xl md:text-7xl" />}
                    <div className={`grid gap-x-6 gap-y-12 ${isLabel ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : isSocial ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' : 'md:grid-cols-2'}`}>
                        {range.map((img, i) => (
                            <Fade key={img.src} delay={(i % 4) * 0.08}>
                            <figure className="group">
                                {isLabel || isSocial ? (
                                    // Formats variés : cadre commun (carré pour les étiquettes, 9:16 pour Instagram), sans recadrage
                                    <div className={`relative overflow-hidden bg-(--pf-ink)/[0.04] ${isLabel ? 'aspect-square' : 'aspect-[9/16]'}`}>
                                        <Image
                                            src={img.src}
                                            alt={img.alt}
                                            fill
                                            sizes="(min-width: 1024px) 25vw, 50vw"
                                            className={`object-contain ease-(--pf-ease) ${isLabel ? 'p-[8%]' : ''} ${
                                                img.bottle
                                                    ? 'transition-[scale,opacity] duration-500 group-hover:scale-[0.6] group-hover:opacity-0'
                                                    : 'transition-transform duration-700 group-hover:scale-[1.04]'
                                            }`}
                                        />
                                        {img.bottle && (
                                            // L'étiquette s'efface, la bouteille monte et se redresse
                                            <Image
                                                src={img.bottle.src}
                                                alt=""
                                                fill
                                                sizes="(min-width: 1024px) 12vw, 25vw"
                                                className="translate-y-[55%] -rotate-6 object-contain py-[6%] opacity-0 drop-shadow-[0_18px_16px_rgba(21,18,15,0.3)] transition-[translate,rotate,opacity] delay-100 duration-700 ease-(--pf-ease) group-hover:translate-y-0 group-hover:rotate-0 group-hover:opacity-100 motion-reduce:translate-y-0 motion-reduce:rotate-0"
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className="overflow-hidden">
                                        <Image src={img.src} alt={img.alt} width={img.w} height={img.h} sizes="(min-width: 1024px) 25vw, 50vw" className="h-auto w-full transition-transform duration-700 ease-(--pf-ease) group-hover:scale-[1.04]" />
                                    </div>
                                )}
                                {img.caption && (
                                    <figcaption className="mt-3 text-sm transition-[translate,color] duration-500 ease-(--pf-ease) group-hover:translate-x-1 group-hover:text-(--pf-wine)">
                                        {t(img.caption)}
                                        {img.bottle && (
                                            <span className="ml-2 text-(--pf-ink)/60 opacity-0 transition-opacity delay-200 duration-500 group-hover:opacity-100">
                                                · {t(ui.onBottle)}
                                            </span>
                                        )}
                                    </figcaption>
                                )}
                            </figure>
                            </Fade>
                        ))}
                    </div>
                </section>
            )}

            {p.materials && (
                <section className="px-4 pb-24 md:px-10">
                    <Words text={t(ui.materials)} className="mb-10 border-t border-(--pf-ink)/20 pt-6 font-(family-name:--pf-serif) text-5xl md:text-7xl" />
                    <div className="grid gap-x-6 gap-y-12 md:grid-cols-2">
                        {p.materials.map((img, i) => (
                            <Fade key={img.src} delay={(i % 2) * 0.1}>
                            <figure className="group">
                                <div className="overflow-hidden">
                                    <Image src={img.src} alt={img.alt} width={img.w} height={img.h} sizes="(min-width: 768px) 50vw, 100vw" className="h-auto w-full transition-transform duration-700 ease-(--pf-ease) group-hover:scale-[1.04]" />
                                </div>
                                {img.caption && <figcaption className="mt-3 text-sm transition-[translate,color] duration-500 ease-(--pf-ease) group-hover:translate-x-1 group-hover:text-(--pf-wine)">{t(img.caption)}</figcaption>}
                            </figure>
                            </Fade>
                        ))}
                    </div>
                </section>
            )}

            <Link href={`/portfolio/${next.slug}?lang=${lang}`} className="group block border-t border-(--pf-ink) px-4 py-20 md:px-10 md:py-28">
                <span className="mb-4 inline-flex items-center gap-2 text-sm text-(--pf-ink)/70">
                    {t(ui.next)} <Arrow />
                </span>
                <span className="block font-(family-name:--pf-serif) text-[14vw] leading-none transition-[translate] duration-700 ease-(--pf-ease) group-hover:translate-x-6 md:text-[10vw]">
                    <Wave text={next.title} />
                </span>
            </Link>
        </main>
    );
}
